import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Send, Pencil, Check, X, Play, Heart, LogOut } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import MobileNav from '@/components/MobileNav';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get full media URL with iOS-compatible transformations for videos
const getMediaUrl = (url, isVideo = false) => {
  if (!url) return '';
  // If it's a Cloudinary URL and it's a video, add iOS-compatible transformations
  if (url.includes('res.cloudinary.com') && isVideo) {
    const transformations = 'vc_h264,f_mp4';
    if (url.includes('vc_h264')) return url;
    if (url.includes('/upload/v')) {
      return url.replace('/upload/v', `/upload/${transformations}/v`);
    }
  }
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const auth = useAuth();
  const { user: currentUser, refreshUser, logout } = auth;
  const isPerformer = typeof auth.isPerformer === 'function' ? auth.isPerformer() : false;
  const messagesEndRef = useRef(null);
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editBio, setEditBio] = useState('');
  const [saving, setSaving] = useState(false);
  const [userPosts, setUserPosts] = useState([]);
  const [activeTab, setActiveTab] = useState('posts'); // 'posts' or 'chat'
  
  // Chat state
  const [threadId, setThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [chatPartner, setChatPartner] = useState(null);

  const isOwnProfile = !userId || userId === currentUser?.id;
  const profileUserId = userId || currentUser?.id;

  useEffect(() => {
    if (isOwnProfile) {
      setProfile(currentUser);
      setEditBio(currentUser?.bio || '');
      setLoading(false);
      
      // For performers viewing their own profile, load chat with admin
      if (currentUser?.role === 'performer') {
        fetchAdminThread();
      }
    } else {
      fetchProfile();
      fetchOrCreateThread();
    }
    
    // Fetch user's posts
    if (profileUserId) {
      fetchUserPosts();
    }
  }, [userId, currentUser, isOwnProfile, profileUserId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchUserPosts = async () => {
    try {
      const response = await axios.get(`${API}/profile/${profileUserId}/posts`);
      setUserPosts(response.data);
    } catch (error) {
      console.log('No posts found');
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile/${userId}`);
      setProfile(response.data);
      setChatPartner(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminThread = async () => {
    try {
      // Create or get thread with admin (performer initiating)
      const response = await axios.post(`${API}/messages/threads`);
      setThreadId(response.data.id);
      setChatPartner({ 
        id: response.data.admin_id, 
        email: response.data.admin_email,
        display_name: null 
      });
      
      // Fetch messages
      const msgResponse = await axios.get(`${API}/messages/threads/${response.data.id}`);
      setMessages(msgResponse.data);
    } catch (error) {
      console.log('No admin thread yet');
    }
  };

  const fetchOrCreateThread = async () => {
    if (!userId || isOwnProfile) return;
    
    try {
      // Create or get existing thread with this user
      const params = currentUser.role === 'admin' 
        ? { performer_id: userId }
        : { admin_id: userId };
      
      const response = await axios.post(`${API}/messages/threads`, null, { params });
      setThreadId(response.data.id);
      
      // Fetch messages
      const msgResponse = await axios.get(`${API}/messages/threads/${response.data.id}`);
      setMessages(msgResponse.data);
    } catch (error) {
      console.log('No existing thread');
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/profile`, {
        bio: editBio.trim() || null
      });
      toast.success('Profile updated');
      setEditing(false);
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      let currentThreadId = threadId;
      
      // Create thread if doesn't exist
      if (!currentThreadId) {
        let params = {};
        if (isOwnProfile && isPerformer) {
          // Performer messaging admin from own profile
          params = {};
        } else if (currentUser.role === 'admin') {
          params = { performer_id: userId };
        } else {
          params = { admin_id: userId };
        }
        const threadResponse = await axios.post(`${API}/messages/threads`, null, { params });
        currentThreadId = threadResponse.data.id;
        setThreadId(currentThreadId);
        setChatPartner({
          id: threadResponse.data.admin_id,
          email: threadResponse.data.admin_email
        });
      }
      
      const response = await axios.post(`${API}/messages/threads/${currentThreadId}`, {
        text: newMessage.trim()
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const getDisplayName = (p) => p?.display_name || p?.email?.split('@')[0] || 'User';
  const getChatPartnerName = () => {
    if (isOwnProfile && isPerformer) {
      return chatPartner ? getDisplayName(chatPartner) : 'Admin';
    }
    return getDisplayName(chatPartner || profile);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-neon-pink border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Determine if we should show chat
  const showChat = !isOwnProfile || (isOwnProfile && isPerformer);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-black flex flex-col" data-testid="profile-page">
      {/* Header */}
      <div className="flex-shrink-0 bg-black/95 backdrop-blur-sm border-b border-zinc-800">
        <div className="flex items-center gap-3 px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="text-white p-2 -ml-2"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1 className="font-heading text-lg font-bold text-white flex-1">
            {isOwnProfile ? 'MY PROFILE' : 'PROFILE'}
          </h1>
          {isOwnProfile && !editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-neon-pink p-2"
              data-testid="edit-profile-btn"
            >
              <Pencil className="w-5 h-5" />
            </button>
          )}
          {isOwnProfile && (
            <button
              onClick={handleLogout}
              className="text-zinc-400 p-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Profile Section */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-neon-pink to-neon-cyan flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-2xl">
              {getDisplayName(profile)[0]?.toUpperCase()}
            </span>
          </div>
          
          <div className="flex-1 min-w-0">
            {/* Username - NOT editable */}
            <h2 className="text-white text-xl font-bold truncate">
              @{getDisplayName(profile)}
            </h2>
            <p className="text-zinc-500 text-sm">{userPosts.length} posts</p>
            
            {editing ? (
              <div className="mt-3 space-y-3">
                <Textarea
                  value={editBio}
                  onChange={(e) => setEditBio(e.target.value)}
                  placeholder="Character description..."
                  className="bg-zinc-900 border-zinc-800 text-white min-h-[80px] resize-none"
                  data-testid="edit-bio-input"
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-neon-pink hover:bg-neon-pink/90"
                    data-testid="save-profile-btn"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setEditing(false);
                      setEditBio(profile?.bio || '');
                    }}
                    className="text-zinc-400"
                    data-testid="cancel-edit-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-zinc-300 text-sm mt-2">
                {profile?.bio || (isOwnProfile ? 'Tap edit to add a character description' : 'No description')}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-zinc-800">
        <button
          onClick={() => setActiveTab('posts')}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === 'posts' 
              ? 'text-white border-b-2 border-neon-pink' 
              : 'text-zinc-500'
          }`}
        >
          Posts
        </button>
        {showChat && (
          <button
            onClick={() => setActiveTab('chat')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'chat' 
                ? 'text-white border-b-2 border-neon-pink' 
                : 'text-zinc-500'
            }`}
          >
            {isOwnProfile && isPerformer ? 'Chat with Admin' : 'Chat'}
          </button>
        )}
      </div>

      {/* Posts Grid */}
      {activeTab === 'posts' && (
        <div className="flex-1 overflow-y-auto pb-20">
          {userPosts.length === 0 ? (
            <p className="text-center text-zinc-600 py-12">No posts yet</p>
          ) : (
            <div className="grid grid-cols-3 gap-0.5 p-0.5">
              {userPosts.map((post) => (
                <div
                  key={post.id}
                  onClick={() => navigate(`/?id=${post.id}`)}
                  className="relative aspect-[9/16] bg-zinc-900 cursor-pointer group"
                  data-testid={`post-${post.id}`}
                >
                  {post.media_type === 'video' ? (
                    <>
                      {post.poster ? (
                        <img
                          src={post.poster}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <video
                          src={getMediaUrl(post.media_url, true)}
                          className="w-full h-full object-cover"
                          muted
                          playsInline
                          preload="auto"
                        />
                      )}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <Play className="w-8 h-8 text-white/80" />
                      </div>
                    </>
                  ) : (
                    <img
                      src={getMediaUrl(post.media_url)}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute bottom-1 left-1 flex items-center gap-1 text-white text-xs bg-black/50 px-1.5 py-0.5 rounded">
                    <Heart className="w-3 h-3" />
                    {post.likes_count}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Chat Section */}
      {activeTab === 'chat' && showChat && (
        <>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {messages.length === 0 ? (
              <p className="text-center text-zinc-600 py-8">
                {isOwnProfile && isPerformer 
                  ? 'Start a conversation with admin'
                  : `Start a conversation with ${getChatPartnerName()}`
                }
              </p>
            ) : (
              messages.map((message) => {
                const isOwnMessage = message.sender_id === currentUser.id;
                return (
                  <div
                    key={message.id}
                    className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                    data-testid={`message-${message.id}`}
                  >
                    <div
                      className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                        isOwnMessage
                          ? 'bg-neon-pink text-white rounded-br-md'
                          : 'bg-zinc-800 text-white rounded-bl-md'
                      }`}
                    >
                      <p className="text-sm">{message.text}</p>
                      <p className={`text-xs mt-1 ${isOwnMessage ? 'text-white/60' : 'text-zinc-500'}`}>
                        {formatTime(message.created_at)}
                      </p>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div className="flex-shrink-0 p-4 bg-black border-t border-zinc-800 mb-16">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder={isOwnProfile && isPerformer 
                  ? "Message admin..."
                  : `Message ${getChatPartnerName()}...`
                }
                className="flex-1 bg-zinc-900 border-zinc-800 text-white"
                data-testid="profile-message-input"
              />
              <Button
                type="submit"
                disabled={sending || !newMessage.trim()}
                className="bg-neon-pink hover:bg-neon-pink/90"
                data-testid="send-profile-message-btn"
              >
                <Send className="w-5 h-5" />
              </Button>
            </form>
          </div>
        </>
      )}

      <MobileNav />
    </div>
  );
};

export default ProfilePage;
